import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './DocumentosRelacionados.css';

// Actualizar la definición del componente para aceptar el nuevo prop
const DocumentosRelacionados = ({ iddoc, isOpen, onClose, onDatosObtenidos }) => {
  const [documentos, setDocumentos] = useState([]);
  const [documentosPorTema, setDocumentosPorTema] = useState({});
  const [temaSeleccionado, setTemaSeleccionado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [estadisticas, setEstadisticas] = useState({});
  const [datosOriginales, setDatosOriginales] = useState(null);
  const [mostrarTodasFechas, setMostrarTodasFechas] = useState(false);

  useEffect(() => {
    if (isOpen && iddoc) {
      cargarDocumentosRelacionados(iddoc);
    }
  }, [isOpen, iddoc]);

  const cargarDocumentosRelacionados = async (documento) => {
    setCargando(true);
    setError(null);

    try {
      console.log(`Consultando documento ID: ${documento} en relaciondocumental`);
      
      // IMPORTANTE: Cambio en la forma de consultar. En lugar de buscar where iddoc === documento,
      // vamos a intentar obtener directamente el documento con ese ID
      
      // Primero intenta obtener el documento directamente por ID
      const docRef = doc(db, "relaciondocumental", documento);
      const docSnap = await getDoc(docRef);
      
      // Si existe como documento directo
      if (docSnap.exists()) {
        console.log("Documento encontrado directamente por ID:", docSnap.data());
        procesarDocumentoEncontrado(docSnap.data());
      } else {
        // Si no existe como documento directo, intenta con la consulta where...
        console.log("Documento no encontrado directamente. Intentando con consulta where...");
        const q = query(collection(db, "relaciondocumental"), 
                       where("iddoc", "==", documento));
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          console.log("No se encontraron documentos para el ID:", documento);
          setDocumentos([]);
          setDocumentosPorTema({todos: []});
          setError(`No se encontraron documentos relacionados para el ID: ${documento}`);
        } else {
          console.log(`Se encontraron ${querySnapshot.size} documentos`);
          querySnapshot.forEach(doc => {
            procesarDocumentoEncontrado(doc.data());
          });
        }
      }
    } catch (error) {
      console.error("Error al cargar documentos relacionados:", error);
      setError("Error al cargar documentos relacionados: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  // Modificar procesarDocumentoEncontrado para compartir los datos con el padre
  const procesarDocumentoEncontrado = (data) => {
    console.log("Procesando documento encontrado con campos:", Object.keys(data));
    setDatosOriginales(data);
    
    let documentosExtraidos = [];
    
    // 1. PRIMERO: Extraer de documentos (que parece ser el formato principal según las capturas)
    if (data.documentos && Array.isArray(data.documentos)) {
      console.log(`Encontrados ${data.documentos.length} elementos en 'documentos'`);
      const docsValidos = data.documentos
        .filter(item => item && typeof item === 'object' && item.texto)
        .map(item => ({
          docId: item.id || item.nombre || 'sin-id',
          texto: item.texto,
          nombre: item.nombre || '',
          fecha: item.fecha || ''
        }));
      
      documentosExtraidos = [...documentosExtraidos, ...docsValidos];
      console.log(`Procesados ${docsValidos.length} documentos válidos`);
    }
    
    // 2. SEGUNDO: Extraer de contenidoTextual (formato alternativo)
    if (documentosExtraidos.length === 0 && data.contenidoTextual && Array.isArray(data.contenidoTextual)) {
      console.log(`Encontrados ${data.contenidoTextual.length} elementos en 'contenidoTextual'`);
      const docsTextualValidos = data.contenidoTextual
        .filter(item => item && typeof item === 'object' && item.texto)
        .map(item => ({
          docId: item.docId || 'sin-id',
          texto: item.texto
        }));
      
      documentosExtraidos = [...documentosExtraidos, ...docsTextualValidos];
      console.log(`Procesados ${docsTextualValidos.length} documentos contenidoTextual válidos`);
    }
    
    // Si no se encontraron documentos en ninguna estructura
    if (documentosExtraidos.length === 0) {
      setError("No se pudieron extraer documentos legibles de la estructura de datos");
      setDocumentos([]);
      return;
    }
    
    setDocumentos(documentosExtraidos);
    
    // Clasificar documentos por tema
    const clasificacion = clasificarDocumentosPorTema(documentosExtraidos);
    setDocumentosPorTema(clasificacion.temas);
    setEstadisticas(clasificacion.estadisticas);
    
    // NUEVO: Compartir datos con el componente padre
    if (onDatosObtenidos && typeof onDatosObtenidos === 'function') {
      onDatosObtenidos({
        documentos: documentosExtraidos,
        temas: clasificacion.temas,
        estadisticas: clasificacion.estadisticas
      });
    }
  };

  const clasificarDocumentosPorTema = (documentos) => {
    // Objeto para almacenar documentos por tema
    const temas = {
      todos: [...documentos], // Todos los documentos sin filtrar
      acuerdoGeneral: [],
      empresasSuscriptoras: [],
      empresasAdhieren: [],
      compromisos: [],
      condicionesEconomicas: [],
      viaticos: [],
      escalafon: [],
      participacion: [],
      otros: []
    };
    
    // Estadísticas
    const estadisticas = {
      totalDocumentos: documentos.length,
      porTema: {},
      fechas: new Set()
    };

    // Patrones para clasificación
    const patrones = {
      acuerdoGeneral: [
        /ACUERDO MARCO SECTORIAL/i,
        /SINTRAELECOL planteó/i,
        /Constitución Política Nacional/i
      ],
      empresasSuscriptoras: [
        /EMPRESAS QUE SUSCRIBEN/i,
        /ARCHIPIELAGOS POWER/i,
        /CHÍDRAL/i,
        /EMPRESA DE ENERGIA DEL PACÍFICO/i
      ],
      empresasAdhieren: [
        /EMPRESAS QUE ADHIEREN/i,
        /GUSTAVO ADOLFO JAY PANG/i
      ],
      compromisos: [
        /COMPROMISOS/i,
        /CAMPO DE APLICACIÓN/i,
        /se aplicará en las empresas/i
      ],
      condicionesEconomicas: [
        /incrementará en el porcentaje/i,
        /VIGENCIA/i,
        /convenciones colectivas, será de dos/i
      ],
      viaticos: [
        /VIATICOS, AUXILIOS/i,
        /fondos médicos/i,
        /ayudas subvenciones/i
      ],
      escalafon: [
        /ESCALAFON/i,
        /Comité Consultivo Nacional sobre escalafón/i
      ],
      participacion: [
        /ACUERDO DE PARTICIPACIÓN/i,
        /procedimiento sumario/i,
        /Ministerio de Minas y Energía informará a Sintraelecol/i
      ]
    };

    // Función para detectar fechas en textos
    const detectarFechas = (texto) => {
      if (!texto) return;
      
      const patronesFecha = [
        /(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+(\d{4})/gi,
        /(\d{1,2})\/(\d{1,2})\/(\d{4})/g,
        /(\d{4})-(\d{1,2})-(\d{1,2})/g,
        /marzo\s+de\s+1998/gi,
        /MARZO\s+6\s+DE\s+1998/i
      ];

      patronesFecha.forEach(patron => {
        const matches = texto.match(patron);
        if (matches) {
          matches.forEach(match => {
            estadisticas.fechas.add(match);
          });
        }
      });
    };

    // Clasificar cada documento
    documentos.forEach(doc => {
      if (!doc || !doc.texto) return;
      
      const texto = doc.texto;
      let clasificado = false;
      
      // Detectar fechas
      detectarFechas(texto);
      
      // Clasificar según patrones
      for (const [tema, patronesTema] of Object.entries(patrones)) {
        if (patronesTema.some(patron => patron.test(texto))) {
          temas[tema].push(doc);
          clasificado = true;
          break; // Asignar solo al tema más relevante
        }
      }
      
      // Si no coincide con ningún patrón, va a "otros"
      if (!clasificado) {
        temas.otros.push(doc);
      }
    });
    
    // Calcular estadísticas por tema
    for (const tema in temas) {
      if (tema !== 'todos') {
        estadisticas.porTema[tema] = temas[tema].length;
      }
    }
    
    return { temas, estadisticas };
  };

  const filtrarDocumentosPorBusqueda = (documentos) => {
    if (!busqueda) return documentos;
    
    return documentos.filter(doc => 
      doc.texto && doc.texto.toLowerCase().includes(busqueda.toLowerCase())
    );
  };
  
  const getDocumentosFiltrados = () => {
    const documentosTema = temaSeleccionado === 'todos' 
      ? documentos 
      : documentosPorTema[temaSeleccionado] || [];
      
    return filtrarDocumentosPorBusqueda(documentosTema);
  };

  // Eliminar la función original renderizarContenido y mantener solo la versión mejorada

  // Agregar esta nueva función para procesar mejor el texto
  const procesarTextoParaVisualizacion = (texto) => {
    if (!texto) return null;
    
    // 1. Detectar párrafos reales (separados por doble salto de línea o más)
    const parrafos = texto.split(/\n\s*\n+/).filter(p => p.trim());
    
    // 2. Para cada párrafo, eliminar saltos de línea simples dentro del mismo texto
    return parrafos.map((parrafo, idx) => {
      // Preservar sangrías al inicio de los párrafos
      const espaciosAlInicio = parrafo.match(/^\s+/);
      const sangria = espaciosAlInicio ? espaciosAlInicio[0].length * 0.25 : 0;
      
      // Determinar si el párrafo parece un título
      const esTitulo = parrafo.trim().toUpperCase() === parrafo.trim() && 
                      parrafo.length < 100 &&
                      !parrafo.includes(',');
      
      // Procesar el contenido del párrafo
      const contenido = parrafo
        .replace(/\n/g, ' ')          // Reemplazar saltos de línea por espacios
        .replace(/\s{2,}/g, ' ')      // Eliminar espacios múltiples
        .trim();                       // Eliminar espacios al inicio/fin
    
      // Aplicar estilo según tipo de contenido
      return (
        <p 
          key={idx} 
          style={{
            marginBottom: esTitulo ? '1rem' : '0.5rem',
            marginTop: esTitulo ? '1rem' : '0.5rem',
            fontWeight: esTitulo ? 'bold' : 'normal',
            paddingLeft: `${sangria}em`,
            textAlign: esTitulo ? 'center' : 'left'
          }}
        >
          {contenido}
        </p>
      );
    });
  };

  // 1. Función para determinar el período de fechas
  const getPeriodoFechas = (fechas) => {
    if (!fechas || fechas.size === 0) return "No disponible";
    
    const fechasArray = Array.from(fechas)
      .map(fechaStr => {
        // Intentar convertir en fechas reales usando diferentes formatos
        const regexFormato1 = /(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+(\d{4})/i;
        const regexFormato2 = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
        const regexFormato3 = /(\d{4})-(\d{1,2})-(\d{1,2})/;
        const regexFormato4 = /marzo\s+de\s+(\d{4})/i;
        const regexFormato5 = /marzo\s+(\d{1,2})\s+de\s+(\d{4})/i;
        
        // Mapeo de nombres de meses a números
        const meses = {
          'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
          'julio': 6, 'agosto': 7, 'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
        };
        
        let fecha;
        
        if (regexFormato1.test(fechaStr)) {
          const match = fechaStr.match(regexFormato1);
          fecha = new Date(parseInt(match[3]), meses[match[2].toLowerCase()], parseInt(match[1]));
        } else if (regexFormato2.test(fechaStr)) {
          const match = fechaStr.match(regexFormato2);
          fecha = new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
        } else if (regexFormato3.test(fechaStr)) {
          const match = fechaStr.match(regexFormato3);
          fecha = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
        } else if (regexFormato4.test(fechaStr)) {
          const match = fechaStr.match(regexFormato4);
          fecha = new Date(parseInt(match[1]), 2, 15); // Marzo, día medio del mes
        } else if (regexFormato5.test(fechaStr)) {
          const match = fechaStr.match(regexFormato5);
          fecha = new Date(parseInt(match[2]), 2, parseInt(match[1]));
        } else {
          // Si no se puede parsear, utilizar el string original
          return { original: fechaStr, fecha: new Date(0) };
        }
        
        return { original: fechaStr, fecha };
      })
      .filter(f => f.fecha && !isNaN(f.fecha));
    
    if (fechasArray.length === 0) return "No se pudo determinar";
    
    // Ordenar fechas
    fechasArray.sort((a, b) => a.fecha - b.fecha);
    
    // Mostrar el rango
    const fechaInicial = fechasArray[0];
    const fechaFinal = fechasArray[fechasArray.length - 1];
    
    // Si es la misma fecha o solo hay una
    if (fechasArray.length === 1 || 
        fechaInicial.fecha.getTime() === fechaFinal.fecha.getTime()) {
      return fechaInicial.original;
    }
    
    return `${fechaInicial.original} - ${fechaFinal.original}`;
  };

  // 2. Función para renderizar el contenido
  const renderizarContenido = () => {
    const documentosFiltrados = getDocumentosFiltrados();
    
    if (documentosFiltrados.length === 0) {
      return (
        <div className="sin-resultados">
          <p>No se encontraron documentos para los criterios seleccionados</p>
          {datosOriginales && (
            <div className="datos-debug">
              <p>Información de depuración (estructura encontrada):</p>
              <pre>{JSON.stringify(datosOriginales, null, 2).substring(0, 500)}...</pre>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="documentos-lista">
        {documentosFiltrados.map((doc, index) => (
          <div key={index} className="documento-item">
            <div className="documento-id">ID: {doc.docId}</div>
            <div className="documento-contenido">
              {procesarTextoParaVisualizacion(doc.texto)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 3. Función para exportar documento
  const exportarDocumento = () => {
    const documentosFiltrados = getDocumentosFiltrados();
    
    if (!documentosFiltrados || documentosFiltrados.length === 0) {
      return "No hay contenido para exportar";
    }
    
    // Combinar todos los textos para exportar
    return documentosFiltrados.map(doc => {
      const encabezado = `==== DOCUMENTO ID: ${doc.docId} ====\n\n`;
      const texto = doc.texto || "Sin contenido";
      
      return encabezado + texto;
    }).join("\n\n" + "=".repeat(40) + "\n\n");
  };

  if (!isOpen) return null;

  return (
    <div className="documentos-relacionados-container">
      <div className="documentos-relacionados-header">
        <h2>Documentos Relacionados - ID: {iddoc}</h2>
        <button className="cerrar-btn" onClick={onClose}>×</button>
      </div>
      
      {/* Todo el contenido desplazable ahora va dentro de documentos-relacionados-content */}
      <div className="documentos-relacionados-content">
        <div className="contenido-scrollable">
          {/* 1. Filtros */}
          {documentos.length > 0 && (
            <div className="filtros-container">
              <div className="filtro-tema">
                <label>Tema:</label>
                <select 
                  value={temaSeleccionado} 
                  onChange={(e) => setTemaSeleccionado(e.target.value)}
                >
                  <option value="todos">Todos los temas</option>
                  <option value="acuerdoGeneral">Acuerdo General</option>
                  <option value="empresasSuscriptoras">Empresas que Suscriben</option>
                  <option value="empresasAdhieren">Empresas que Adhieren</option>
                  <option value="compromisos">Compromisos y Campo de Aplicación</option>
                  <option value="condicionesEconomicas">Condiciones Económicas</option>
                  <option value="viaticos">Viáticos y Auxilios</option>
                  <option value="escalafon">Escalafón</option>
                  <option value="participacion">Acuerdo de Participación</option>
                  <option value="otros">Otros</option>
                </select>
              </div>
              
              <div className="filtro-busqueda">
                <input
                  type="text"
                  placeholder="Buscar en los documentos..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
                <button onClick={() => setBusqueda('')}>Limpiar</button>
              </div>
            </div>
          )}
          
          {/* 2. Estadísticas */}
          {documentos.length > 0 && (
            <div className="estadisticas-container">
              <h3>Estadísticas</h3>
              <div className="estadisticas-grid">
                <div className="estadistica-item">
                  <strong>Total documentos:</strong> {estadisticas.totalDocumentos || 0}
                </div>
                
                {estadisticas.fechas && estadisticas.fechas.size > 0 && (
                  <div className="estadistica-item fechas-container">
                    <div className="fechas-header">
                      <strong>Fechas detectadas:</strong> 
                      <span className="fechas-count">({estadisticas.fechas.size})</span>
                      <button 
                        className="toggle-fechas-btn"
                        onClick={() => setMostrarTodasFechas(!mostrarTodasFechas)}
                      >
                        {mostrarTodasFechas ? 'Ocultar fechas' : 'Ver fechas'}
                      </button>
                    </div>
                    
                    {mostrarTodasFechas && (
                      <div className="fechas-grid">
                        {Array.from(estadisticas.fechas || []).sort().map((fecha, index) => (
                          <span key={index} className="fecha-item">{fecha}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {estadisticas.fechas && estadisticas.fechas.size > 0 && (
                  <div className="estadistica-item">
                    <strong>Período:</strong> {getPeriodoFechas(estadisticas.fechas)}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* 3. Contenido principal */}
          {cargando && <div className="cargando">Cargando documentos...</div>}
          {error && <div className="error">{error}</div>}
          {!cargando && !error && renderizarContenido()}
        </div>
      </div>
      
      {/* La barra de acciones permanece abajo fija */}
      {documentos.length > 0 && (
        <div className="documentos-acciones">
          <button 
            onClick={() => {navigator.clipboard.writeText(exportarDocumento())}}
            className="copiar-btn"
          >
            Copiar documento completo
          </button>
        </div>
      )}
    </div>
  );
};

export default DocumentosRelacionados;